
import React, { PropsWithChildren, useEffect } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './MarketingClaims.css';
import { Col, Container, Row, TextLine, Title } from '../../importCL';
import PaneCashBack from '../PaneCashBack/PaneCashBack';
import { useWidthContext } from '../../providers/Width';
import { equalizeHeights } from '../../utils/cob-utils';

const DEFAULT_IMAGE_WIDTH = 480;
const DEFAULT_IMAGE_HEIGHT = 360;
const DEFAULT_ICON_IMAGE_WIDTH = 64;
const DEFAULT_ICON_IMAGE_HEIGHT = 64;

interface IClaimsCard {
    image?: string;
    mobileImage?: string;
    imgAltText?: string;
    mobileImgAltText?: string;
    headline?: string;
    subHeading?: string;
    description?: string;
    numberStats?: string;
    apiStats?: boolean;
}

interface IMarketingClaimsProps extends MappedComponentProperties {
    claimsCards?: IClaimsCard[];
    heading?: string;
    subHeading?: string;
}

export const MarketingClaimsEditConfig = {
    emptyLabel: 'Marketing Claims',
    isEmpty: (props: PropsWithChildren<IMarketingClaimsProps>) => {
        return !props || !props.claimsCards;
    },
};

const MarketingClaims = (props: PropsWithChildren<IMarketingClaimsProps>): JSX.Element => {
    
    const { width } = useWidthContext();
    const isMobile = width <= 768;

    const equalizeTiles = () => {
        if (!isMobile) {
            equalizeHeights(['.MarketingClaims__tiles', '.MarketingClaims__twoBgTile']);
        }
    };

    useEffect(() => {
        const equalizeTimeout = setTimeout(equalizeTiles, 100);

        return () => clearTimeout(equalizeTimeout);
    }, []);

    useEffect(() => {
        equalizeTiles();
    }, [width, isMobile]);

    return (
        <Container id='marketing-claims' className='MarketingClaims !py-16'>
            { props.heading &&
                <TextLine 
                    variant='author'
                    tag='h2'
                    subHeading={props.subHeading ? transformHTMLParserLinks(props.subHeading) : ''}
                    className='mb-6'
                >
                    {transformHTMLParserLinks(props.heading)}
                </TextLine>
            }
            <Row className={`MarketingClaims__row ${props.heading? 'pt-8' : ''}`} justify='start'>
                {props.claimsCards?.length <= 2 ? props.claimsCards.map((claimsCard: IClaimsCard, index: number) => (
                    <Col key={index} sm={12} md={props.claimsCards?.length === 1 ? 12 : 6} lg={props.claimsCards?.length === 1 ? 12 : 6}>
                        <div className={`MarketingClaims__${props.claimsCards?.length === 1 ? 'one' : 'two'}BgTile`} style={isMobile ? { height: 'unset' } : {}}>
                            <img src={!isMobile ? claimsCard.image : claimsCard.mobileImage} alt={!isMobile ? claimsCard.imgAltText : claimsCard.mobileImgAltText} width={DEFAULT_IMAGE_WIDTH} height={DEFAULT_IMAGE_HEIGHT}/>
                            <div className={`MarketingClaims__${props.claimsCards?.length === 1 ? 'one' : 'two'}TextContainer`}>
                                <Title className={`pl-4 MarketingClaims__${props.claimsCards?.length === 1 ? 'largeHeadline' : 'mediumHeadline'}`} tag='h3'>
                                    {transformHTMLParserLinks(claimsCard.headline)}
                                </Title>
                                {/* @ts-ignore -- Using PaneCashback as non-AEM-component */}
                                {claimsCard.apiStats ? <PaneCashBack claimsCardHeadline={claimsCard.headline} /> : <Title className={`MarketingClaims__${props.claimsCards?.length === 1 ? 'one' : 'two'}Statistic`}>{transformHTMLParserLinks(claimsCard.numberStats)}</Title>}
                                <p className='MarketingClaims__description'>
                                    {transformHTMLParserLinks(claimsCard.description)}
                                </p>
                            </div>
                        </div>
                    </Col>
                )) : 
                    props.claimsCards && (props.claimsCards.slice(0, 6)).map((claimsCard: IClaimsCard, index: number) => (
                        <Col key={index} xs={6} sm={12} md={12} lg={props.claimsCards?.length === 4 ? { span: 4, offset: (index + 1) % 2 !== 0 ? 2 : undefined } : 4}>
                            <div className='MarketingClaims__wrapper'>
                                <img src={claimsCard.image} alt={claimsCard.imgAltText} width={DEFAULT_ICON_IMAGE_WIDTH} height={DEFAULT_ICON_IMAGE_HEIGHT}/>
                                <div className='MarketingClaims__tiles'>
                                    <div className='MarketingClaims__text'>
                                        <Title className='MarketingClaims__statistics' tag='h3'>
                                            {transformHTMLParserLinks(claimsCard.numberStats)}
                                        </Title>
                                        <p className='MarketingClaims__description'>
                                            {transformHTMLParserLinks(claimsCard.description)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    ))}
            </Row>
        </Container>
    );
};

export default MarketingClaims;
